import React, { Component } from 'react';
import ThemedIcon from './ThemedIcon';
import { connect } from 'react-redux';

import Modal from '../../containers/common/Modal';
import { FlyInTransition } from '../../components/transitions/transitions';
import { ADDRESS_MODAL_CONFIG } from '../../utils/address';
import { logEvent } from '../../utils/logEvent';
import LabLocation from '../../containers/lab-test/LabLocation';
import LoadingComponent from './LoadingComponent';
import FooterButton from './FooterButton';
import { PrimaryButton } from './Buttons';

import checkedIcon from '../../images/pharmacy/checked.svg';
import plusIcon from '../../images/pharmacy/plus.svg';
import homeIcon from '../../images/pharmacy/home.svg';
import crossIcon from '../../images/common/cross.svg';
import cautionIcon from '../../images/lab-test/caution-new.svg';
import cautionIconBlue from '../../images/lab-test/caution-new-blue.svg';
import trashIcon from '../../images/common/trashIcon.svg';
import editIcon from '../../images/edit.svg';
/**
 * Address Modal Config
 */
const LAB_NOT_SERVICEABLE_ADDRESS_TEXT =
  'Selected Lab Partner is not available at this location';
const PHARMACY_NOT_SERVICEABLE_ADDRESS_TEXT = 'This address is not serviceable';
const NOT_UPDATED_ADDRESS_TEXT = 'Please update address.';

const ADDRESS_TYPES = [
  {
    id: 1,
    key: 'home',
    title: 'Home',
  },
  {
    id: 2,
    key: 'work',
    title: 'Work',
  },
  {
    id: 3,
    key: 'other',
    title: 'Other',
  },
];
export class AddressModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      addressList: [],
      addressError: false,
      showModal: true,
      showNewAddress: false,
      showMapModal: false,
      updateAddress: {},
      address: '',
      locality: '',
      pincode: '',
      addressType: '',
      addressToEdit: '',
      lat: '',
      long: '',
      selectingAddress: false,
      addressConfig: {
        allowEditAddress: true,
        allowDeleteAddress: true,
        allowAddAddress: true,
      },
      selectedAddress: null || this.props.selectedAddress,
    };
    this.isLabs = this.props.serviceLine;
  }

  componentDidMount() {
    this.getAddress();
  }
  emptyFormStates = () => {
    this.setState({
      address: '',
      locality: '',
      pincode: '',
      addressType: '',
      addressToEdit: '',
    });
  };
  hideModal = () => {
    if (this.state.showNewAddress) {
      this.emptyFormStates();
      return this.setState({
        showModal: true,
        showNewAddress: false,
        tobeUpdatedAddress: {},
      });
    }
    this.setState({ showModal: false });
    setTimeout(this.props.hideModal, 200);
  };

  getAddress = async () => {
    this.setState({ loading: true });
    const { serviceLine, cartId } = this.props;

    try {
      let res = await ADDRESS_MODAL_CONFIG[serviceLine].apiFunction(
        this.props.authToken,
        cartId
      );

      const {
        filterNonServiceable,
        filterNonUpdatedAddress,
        addressIsSelectable,
        addressIsUpdated,
      } = ADDRESS_MODAL_CONFIG[serviceLine];
      if (filterNonServiceable) {
        res.addresses = res.addresses.filter(addressIsSelectable);
      }
      // A screen can opt out of hiding addresses that have no
      // coordinates yet. This component already renders those with an
      // inline 'update location' action, so filtering them out makes
      // saved addresses look like they have gone missing.
      const hideNonUpdatedAddress =
        this.props.filterNonUpdatedAddress ?? filterNonUpdatedAddress;
      if (hideNonUpdatedAddress) {
        res.addresses = res.addresses.filter(addressIsUpdated);
      }

      this.setState({
        addressList: res.addresses || [],
        config: res.config || this.state.addressConfig,
      });
      // If selectedAddress only has addressId (from supplement flow), resolve
      // the full object.
      if (
        this.state.selectedAddress?.addressId &&
        !this.state.selectedAddress?.address
      ) {
        const fullAddress = (res.addresses || []).find(
          (a) => a.addressId === this.state.selectedAddress.addressId
        );
        if (fullAddress) {
          this.setState({ selectedAddress: fullAddress });
        }
      }

      // Returned so callers can act on the refreshed list (e.g. select the
      // address that was just created) without re-reading state.
      return res.addresses || [];
    } catch (err) {
      this.setState({ addressError: true, addressList: [] });
      return [];
    } finally {
      this.setState({ loading: false });
    }
  };

  // Mirrors the row click handler: an address is only selectable once it has
  // coordinates and the service line considers it serviceable.
  isSelectable = (ele) =>
    Boolean(ele?.isLocationUpdated) &&
    ADDRESS_MODAL_CONFIG[this.props.serviceLine]?.addressIsSelectable(ele);

  handleOutsideClick = (e) => {
    // ignore clicks on the component itself
    if (
      (this.node && this.node.contains(e.target)) ||
      (this.node2 && this.node2.contains(e.target)) ||
      (this.node3 && this.node3.contains(e.target))
    ) {
      return;
    }
    if (this.state.showNewAddress) {
      this.emptyFormStates();
      return this.setState({
        showModal: true,
        showNewAddress: false,
      });
    }
    this.hideModal();
  };

  addNewAddress = () => {
    this.setState({ adding: true });
    const body = {
      title: this.state.addressType,
      address: this.state.address,
      locality: this.state.locality,
      pincode: this.state.pincode,
      addressId: this.state.addressToEdit,
      lat: this.state.lat,
      long: this.state.long,
    };
    const isEdit = Boolean(body.addressId);
    // Captured before the refresh so the newly created address can be told apart
    // from the existing ones; the create response does not reliably carry its id.
    const previousIds = this.state.addressList.map((ele) => ele.addressId);
    let promise;
    if (body.addressId) {
      promise = ADDRESS_MODAL_CONFIG[
        this.props.serviceLine
      ].editAddressFunction(body, this.props.authToken);
      if (this.props.serviceLine === 'labs') {
        logEvent('Labs Address Edit Clicked', {
          cartType: this.props.cartType,
          cartId: this.props.cartId,
          partnerId: this.props.partnerId,
        });
      }
    } else {
      promise = ADDRESS_MODAL_CONFIG[
        this.props.serviceLine
      ].createAddressFunction(body, this.props.authToken);
      if (this.props.serviceLine === 'labs') {
        logEvent('Labs Add Address Clicked', {
          cartType: this.props.cartType,
          cartId: this.props.cartId,
          partnerId: this.props.partnerId,
        });
      }
    }
    promise
      .then(async (res) => {
        const addresses = await this.getAddress();

        if (isEdit) {
          // Re-point the selection at the refreshed object so the row shows the
          // edited text instead of the stale copy it was selected with.
          const { selectedAddress } = this.state;
          const updated = addresses.find(
            (ele) => ele.addressId === selectedAddress?.addressId
          );
          if (updated) {
            this.setState({ selectedAddress: updated });
          }
        } else {
          // Preselect what the user just added - leaving the previous address
          // selected made it look like the new one had been ignored.
          const created = addresses.find(
            (ele) => !previousIds.includes(ele.addressId)
          );
          if (this.isSelectable(created)) {
            this.setState({ selectedAddress: created });
          }
        }

        this.emptyFormStates();
        this.setState({
          showNewAddress: false,
          showModal: true,
        });
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        this.setState({
          adding: false,
        });
      });
  };

  removeAddress = (addressId, address) => {
    const { authToken } = this.props;
    if (addressId === this.props.selectedAddress?.addressId) {
      this.props.selectAddress(null);
    }
    ADDRESS_MODAL_CONFIG[this.props.serviceLine]
      .removeApi(addressId, authToken)
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        this.getAddress();
      });
  };

  editAddress = (ele) => {
    const addressType =
      ele.title === 'home' || ele.title === 'work' ? ele.title : 'other';
    this.setState({
      address: ele.address,
      locality: ele.locality,
      addressType,
      pincode: ele.pincode,
      addressToEdit: ele.addressId,
      showModal: false,
      showMapModal: true,
      showNewAddress: false,
    });
  };

  selectLatLong = async (lat, long, value) => {
    console.log('selectLatLong', lat, long, value);
    if (this.state.updateLocationOnly) {
      const body = {
        ...this.state.tobeUpdatedAddress,
        lat,
        long,
      };
      await ADDRESS_MODAL_CONFIG[this.props.serviceLine].editAddressFunction(
        body,
        this.props.authToken
      );
      this.setState({
        showMapModal: false,
        showModal: false,
        showNewAddress: false,
        updateLocationOnly: false,
      });
      this.props.selectAddress(body);
    } else {
      this.setState({
        lat,
        long,
        showMapModal: false,
        showModal: false,
        showNewAddress: true,
        updateLocationOnly: false,
        pincode: value?.structured_formatting?.postalCode || '',
        locality: value?.structured_formatting?.locality || '',
        address: value?.structured_formatting?.main_text || '',
        addressType: this.state.addressType,
      });
    }
  };

  onMapClose = () => {
    this.setState({
      showMapModal: false,
      showModal: true,
    });
  };

  updateLocationAddress = (ele) => {
    this.setState({
      showMapModal: true,
      showModal: false,
      updateLocationOnly: true,
      tobeUpdatedAddress: ele,
    });
  };

  render() {
    const {
      locality,
      pincode,
      addressType,
      addressList,
      showNewAddress,
      showMapModal,
      loading,
      addressError,
      addressConfig,
      selectedAddress,
    } = this.state;
    const { allowAddAddress, allowDeleteAddress, allowEditAddress } =
      addressConfig;
    const { modalHeaderText, serviceLine } = this.props;

    const renderLabAddress = (ele) => {
      const selected = ele.addressId === selectedAddress?.addressId || false;

      // Labs: If serviceable is not present [when cartId not passed in API] Or if serviceable present and true [when cartId passed in API]
      // Pharmacy: if isServiceable is false, then it is not serviceable
      const selectable =
        ADDRESS_MODAL_CONFIG[serviceLine]?.addressIsSelectable(ele);

      return (
        <div
          className="bg-txtlight-shades4 mb-20px rounded-12"
          key={ele.addressId}
        >
          <div
            className={`flex flex-row items-center justify-between pt-18px pb-12px pl-16px pr-20px clickable rounded-12`}
            style={{
              border: `1px solid ${selected ? 'var(--primary)' : '#f0f3f6'}`,
            }}
            key={ele.addressId}
          >
            <div
              className="flex flex-1 flex-row"
              onClick={() => {
                if (!ele?.isLocationUpdated) {
                  return this.updateLocationAddress(ele);
                }
                if (selectable) {
                  this.setState({
                    selectedAddress: ele,
                  });
                }
              }}
            >
              {selectable ? (
                <div
                  className={`mr-12px rounded-full h-22px w-22px ${selected ? 'bg-primary-button' : 'bg-txtsecondary-shades7'
                    }`}
                ></div>
              ) : (
                ''
              )}
              <div className="flex flex-1 flex-col">
                <span className="text-body-s font-semibold">{ele.address}</span>
                <span className="text-body-s text-txtsecondary font-regular">
                  {ele.pincode}, {ele.title}
                </span>
              </div>
            </div>
            <div className="flex flex-row items-center">
              {allowDeleteAddress && (
                <img
                  src={trashIcon}
                  alt="trash"
                  className="mr-24px h-22px w-22px"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.removeAddress(ele.addressId, ele);
                  }}
                />
              )}
              {allowEditAddress && (
                <img
                  src={editIcon}
                  className="h-24px w-24px"
                  alt="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.editAddress(ele);
                  }}
                />
              )}
            </div>
          </div>
          {(ele.serviceable === false || ele.isServiceable === false) && (
            <div className="p-8px pl-20px flex flex-row items-center border-t border-txtsecondary-shades7 border-solid">
              <img src={cautionIcon} alt="caution" className="h-12px w-12px" />
              <span className="text-chip ml-8px font-medium text-accent2-shades4">
                {serviceLine === 'labs'
                  ? LAB_NOT_SERVICEABLE_ADDRESS_TEXT
                  : PHARMACY_NOT_SERVICEABLE_ADDRESS_TEXT}
              </span>
            </div>
          )}
          {!ele.isLocationUpdated && (
            <div
              onClick={() => this.updateLocationAddress(ele)}
              className="p-8px pl-20px flex flex-row items-center border-t border-txtsecondary-shades7 border-solid"
            >
              <img
                src={cautionIconBlue}
                alt="caution"
                className="h-12px w-12px"
              />
              <span className="text-chip ml-8px font-medium text-secondary-shades5">
                {NOT_UPDATED_ADDRESS_TEXT}
              </span>
            </div>
          )}
          {ADDRESS_MODAL_CONFIG[serviceLine]?.showBottomMessage &&
            ele?.deliveryOptionMsg && (
              <div className="p-8px pl-20px flex flex-row items-center border-t border-txtsecondary-shades7 border-solid">
                <img
                  src={cautionIconBlue}
                  alt="caution"
                  className="h-12px w-12px"
                />
                <span className="text-chip ml-8px font-medium text-secondary-shades5">
                  {ele.deliveryOptionMsg}
                </span>
              </div>
            )}
        </div>
      );
    };

    return (
      <Modal>
        <div
          className="modalContainer address-modal-root"
          onClick={this.handleOutsideClick}
        >
          <FlyInTransition
            in={this.state.showModal}
            mountOnEnter
            unmountOnExit
            appear
          >
            <div
              className="bg-textbox absolute bottom-0px w-full"
              ref={(node) => (this.node = node)}
              style={{
                borderRadius: '12px 12px 0 0',
              }}
            >
              <div className="p-20 border-b border-txtsecondary-shades7 border-solid flex flex-row items-center justify-between">
                <div className="flex flex-row items-center">
                  <img src={homeIcon} alt="home" />
                  <span className="text-title-s ml-10px font-semibold text-txtlight">
                    {modalHeaderText || 'Choose Address'}
                  </span>
                </div>
                <img
                  src={crossIcon}
                  alt="cross"
                  onClick={this.hideModal}
                  className="cursor-pointer"
                />
              </div>

              <div
                className="p-20 overflow-auto "
                style={{
                  minHeight: '150px',
                  height: '50vh',
                  paddingBottom: '150px',
                }}
              >
                {loading && !addressError && <LoadingComponent />}
                {!loading && addressError && (
                  <div className="ml-8px text-body-l text-txtlight font-semibold">
                    Error in getting address
                  </div>
                )}
                {!loading && !addressError && (
                  <>
                    {serviceLine && addressList
                      ?.slice()
                      .sort((a, b) => {
                        const preSelectedId = this.props.selectedAddress?.addressId;
                        if (preSelectedId) {
                          if (a.addressId === preSelectedId) return -1;
                          if (b.addressId === preSelectedId) return 1;
                        }
                        return 0;
                      })
                      .map(renderLabAddress)}
                    {allowAddAddress && (
                      <div
                        className="center cursor-pointer"
                        onClick={() => {
                          this.setState({
                            showModal: false,
                            showMapModal: true,
                          });
                        }}
                      >
                        <img src={plusIcon} alt="add" />
                        <span className="ml-8px text-title-s text-txtlight font-semibold">
                          Add New Address
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              {addressList.length > 0 && (
                <FooterButton
                  buttonText={this.props.buttonText || 'Next'}
                  className="mt-24px"
                  disabled={!this.state.selectedAddress}
                  onClick={() => {
                    this.props.selectAddress(this.state.selectedAddress);
                    if (!this.props.hideNativeOnSelect) this.hideModal();
                  }}
                />
              )}
            </div>
          </FlyInTransition>
          <FlyInTransition
            in={showNewAddress}
            mountOnEnter
            unmountOnExit
            appear
          >
            <div
              className="bg-textbox absolute bottom-0px w-full rounded-t-12"
              ref={(node) => (this.node2 = node)}
            >
              <div className="p-20 border-b border-txtsecondary-shades7 border-solid flex flex-row items-center justify-between">
                <div className="flex flex-row items-center">
                  <img src={homeIcon} alt="home" />
                  <span className="text-title-s ml-10px font-semibold text-txtlight">
                    Add New Address
                  </span>
                </div>
                <img src={crossIcon} alt="cross" onClick={this.hideModal} />
              </div>
              <div className="p-20 max-h-96 overflow-auto">
                <div>
                  <p className="text-body-s text-txtlight font-medium">
                    Address
                  </p>
                  <input
                    className="mt-8px new-address-input text-title-s font-semibold"
                    placeholder="ex: C-24, okhla phase 3, new delhi"
                    value={this.state.address}
                    onChange={(e) => {
                      this.setState({
                        address: e.target.value,
                      });
                    }}
                  />
                </div>
                <div className="mt-24px">
                  <p className="text-body-s text-txtlight font-medium">
                    Pincode
                  </p>
                  <input
                    className="mt-8px new-address-input text-title-s font-semibold"
                    placeholder="100003"
                    value={this.state.pincode}
                    onChange={(e) => {
                      this.setState({
                        pincode: e.target.value,
                      });
                    }}
                  />
                </div>
                <div className="mt-24px">
                  <p className="text-body-s text-txtlight font-medium">
                    Locality
                  </p>
                  <input
                    className="mt-8px new-address-input text-title-s font-semibold"
                    placeholder="near global market"
                    value={this.state.locality}
                    onChange={(e) => {
                      this.setState({
                        locality: e.target.value,
                      });
                    }}
                  />
                </div>
                <div className="mt-24px">
                  <p className="text-body-s text-txtlight font-medium">
                    Address Type
                  </p>
                  <div className="flex flex-row w-full justify-between my-8px">
                    {ADDRESS_TYPES.map((type) => {
                      const selected = this.state.addressType === type.key;
                      return (
                        <span
                          className="text-center relative pt-8px pb-8px border border-txtsecondary-shades7 border-solid rounded-12 pr-24px pl-24px"
                          onClick={() =>
                            this.setState({
                              addressType: type.key,
                            })
                          }
                          style={
                            selected ? { borderColor: 'var(--primary)' } : {}
                          }
                        >
                          <p
                            className="text-body-l font-regular"
                            style={
                              selected
                                ? { color: 'var(--primary)', fontWeight: '600' }
                                : { color: '#302E6B' }
                            }
                          >
                            {type.title}
                          </p>
                          {selected && (
                            <span className="absolute -top-10px -right-10px">
                              <ThemedIcon src={checkedIcon} alt="tick" />
                            </span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </div>
                <PrimaryButton
                  disabled={
                    !(
                      this.state.address.length > 0 &&
                      locality.length > 2 &&
                      String(pincode).length > 5 &&
                      addressType
                    )
                  }
                  className="w-full rounded-40 bg-textbox bg-primary py-12px center text-body-l"
                  style={{ margin: '16px 0' }}
                  loading={this.state.adding}
                  onTap={this.addNewAddress}
                >
                  <span className="text-button-s font-semibold text-textbox mr-10px">
                    {!this.state.adding ? 'Add' : 'Loading...'}
                  </span>
                </PrimaryButton>
              </div>
            </div>
          </FlyInTransition>

          <FlyInTransition in={showMapModal} mountOnEnter unmountOnExit appear>
            <div
              className="bg-textbox absolute bottom-0px w-full h-full rounded-t-12 doctorProfileModal"
              ref={(node) => (this.node3 = node)}
            >
              <LabLocation
                onLocationSelect={this.selectLatLong}
                onClose={this.onMapClose}
              />
            </div>
          </FlyInTransition>
        </div>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken: state.user.authToken,
});

export default connect(mapStateToProps)(AddressModal);
